import Styles from './AutopilotUpgradeEntryModal.module.scss'

import { Modal, StackModalProps, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { UserProfileCircle, UsersProfiles1, ColumnVertical1, Target2, ArrowRight, Close } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Templates } from 'blaze-ui/icons/24/Templates'
import CalendarPost from 'blaze-ui/icons/20/CalendarPost'
import { BlazeAI } from 'almanac-ui/icons'
import { Server02 } from 'blaze-ui/icons/16'
import { trackFreemiumModalCompleted } from './trackFreemiumModalCompleted'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { useRootStore } from 'stores/RootStore'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useMemo } from 'react'
import { findPlanCreditLimit } from 'components/Settings/Workspace/AutopilotPlans/utils'
import { observer } from 'mobx-react-lite'

interface Props extends StackModalProps {
  variant?: 'end_of_trial' | 'out_of_credits' | 'low_on_credits' | 'no_credits' | 'invite_team' | 'more_credits'
  targetPlanKey?: PaymentPlanKey
}

export const AutopilotUpgradeEntryModal = observer(({ variant = 'end_of_trial', targetPlanKey }: Props) => {
  const { openModal, closeModal } = useModals()
  const { tierUsageStore } = useRootStore()
  const { currentSubscription } = tierUsageStore
  let { plan: currentPlan = PaymentPlanKey.BlazeAutopilotStarter } = currentSubscription || {}
  currentPlan =
    currentPlan === PaymentPlanKey.BlazeAutopilotGrowth
      ? PaymentPlanKey.BlazeAutopilotGrowth
      : PaymentPlanKey.BlazeAutopilotStarter
  const planKey = targetPlanKey || currentPlan
  const workspace = useCurrentWorkspace()

  const isGrowthPlan = planKey === PaymentPlanKey.BlazeAutopilotGrowth
  const creditLimit = findPlanCreditLimit(tierUsageStore.autopilotPlans, planKey)

  const handleUpgrade = () => {
    const plan = tierUsageStore.autopilotPlans.find((p) => p.key === planKey)
    if (!plan) return

    trackFreemiumModalCompleted('upgrade')
    openModal(AutopilotPlansModal, {
      initialStep: 'selected',
      initialPlanAndPrice: { plan, price: undefined },
    })
  }

  const handleSeeAllPlans = () => {
    trackFreemiumModalCompleted('upgrade')
    openModal(AutopilotPlansModal, {})
  }

  const handleDismiss = () => {
    trackFreemiumModalCompleted(null)
    if (workspace) {
      WorkspaceClient.completeSetFreemiumBrandPlan(workspace.id)
    }
    closeModal()
  }

  const starterColor = '#EF6800'
  const growthColor = '#0083E2'

  const header = useMemo(() => {
    if (variant === 'out_of_credits') {
      return "You're ready to create more!"
    }
    if (variant === 'low_on_credits') {
      return "You're getting the hang of this."
    }
    if (variant === 'no_credits') {
      return 'You used every last credit.'
    }
    if (variant === 'more_credits') {
      return 'Upgrade to Growth to get more monthly credits'
    }
    if (variant === 'invite_team') {
      return 'Upgrade to Growth to get your whole team involved.'
    }
    return isGrowthPlan
      ? "You've built something worth scaling. Growth gives you the full platform."
      : "You're ready to go. Keep your strategy running with Starter."
  }, [variant, isGrowthPlan])

  const subHeader = useMemo(() => {
    if (variant === 'out_of_credits') {
      return `You've used your trial credits. Upgrade to keep creating with ${creditLimit.toLocaleString()} fresh credits every month.`
    }
    if (variant === 'low_on_credits') {
      return "You've used most of your trial credits, which means Blaze is working. Upgrade to keep creating."
    }
    if (variant === 'no_credits') {
      return 'That means Blaze is working. Upgrade to keep your momentum going.'
    }
    if (variant === 'invite_team') {
      return 'Everything you set up keeps running. Now add the tools to scale it, including team workflows, advanced AI, and insights that compound.'
    }

    return isGrowthPlan
      ? 'Everything you set up keeps running. Now add the tools to scale it, including team workflows, advanced AI, and insights that compound.'
      : 'Everything you built during your trial — your content calendar, your campaigns, your brand voice — stays live and publishing.'
  }, [variant, isGrowthPlan, creditLimit])

  const img = useMemo(() => {
    if (variant === 'out_of_credits' || variant === 'low_on_credits' || variant === 'no_credits') {
      return 'https://res.cloudinary.com/almanac/image/upload/v1769716847/blaze_assets/virtual_marketer/onboarding/credits_upgrade_modal_pujgst.png'
    }

    return isGrowthPlan
      ? 'https://res.cloudinary.com/almanac/image/upload/v1769569791/blaze_assets/virtual_marketer/onboarding/growth_upgrade_modal_pwybni.png'
      : 'https://res.cloudinary.com/almanac/image/upload/v1769569791/blaze_assets/virtual_marketer/onboarding/starter_upgrade_modal_jxawon.png'
  }, [variant, isGrowthPlan])

  return (
    <Modal.Root onPressOutside={handleDismiss} bodyClassName={Styles.bodyClassName} size="lg">
      <div className={Styles.content}>
        <div className={Styles.closeButtonMobile}>
          <IconButton icon={Close} size="lg" variant="tertiary" onPress={handleDismiss} aria-label="Close" />
        </div>
        <div className={Styles.leftPanel}>
          <div className={Styles.leftPanelContent}>
            <div className={Styles.header}>
              <Heading level={1}>{header}</Heading>
              <Text>{subHeader}</Text>
            </div>

            <div className={Styles.freePlanSection}>
              <div className={Styles.freePlanSectionLeft}>
                <Heading level={5}>What you get with {isGrowthPlan ? 'Growth' : 'Starter'}:</Heading>
                {planKey === PaymentPlanKey.BlazeAutopilotStarter && (
                  <Text>
                    <ul>
                      <li>
                        <Templates size={24} color={starterColor} /> A multi-month content strategy that converts.
                      </li>
                      <li>
                        <CalendarPost size={24} color={starterColor} />
                        Scheduling and posting. Automatically, every day.
                      </li>
                      <li>
                        <UserProfileCircle size={24} color={starterColor} />
                        Simple, powerful insights that lead to growth.
                      </li>
                      <li>
                        <BlazeAI size={24} color={starterColor} />
                        Complete control. Full customization.
                      </li>
                      <li>
                        <Server02 size={24} color={starterColor} />
                        {creditLimit.toLocaleString()} AI credits every month.
                      </li>
                    </ul>
                  </Text>
                )}
                {planKey === PaymentPlanKey.BlazeAutopilotGrowth && (
                  <Text>
                    <ul>
                      <li>
                        <Templates size={24} color={growthColor} /> Post on every channel, everywhere.
                      </li>
                      <li>
                        <UsersProfiles1 size={24} color={growthColor} />
                        Bring your whole team with built-in approvals.
                      </li>
                      <li>
                        <ColumnVertical1 size={24} color={growthColor} />
                        Run multiple campaigns simultaneously.
                      </li>
                      <li>
                        <Target2 size={24} color={growthColor} />
                        Get AI that optimizes your paid spend automatically.
                      </li>
                      <li>
                        <Server02 size={24} color={growthColor} />
                        {creditLimit.toLocaleString()} credits/month to create at scale
                      </li>
                    </ul>
                  </Text>
                )}
              </div>
              <div></div>
            </div>
          </div>
          <div className={Styles.buttonContainer}>
            <Button size="lg" onPress={handleSeeAllPlans} aria-label="See all plans" variant="tertiary">
              Compare Plans
            </Button>
            <div className={Styles.inline}>
              <Button size="lg" onPress={handleUpgrade} endIcon={ArrowRight} fullWidth>
                Activate {planKey === PaymentPlanKey.BlazeAutopilotGrowth ? 'Growth' : 'Starter'}
              </Button>
            </div>
          </div>
        </div>

        <div className={Styles.rightPanel}>
          <img src={img} alt="Campaign Dashboard" className={Styles.backgroundImage} />
          <div className={Styles.closeButton}>
            <IconButton icon={Close} size="lg" variant="tertiary" onPress={handleDismiss} aria-label="Close" />
          </div>
        </div>
      </div>
    </Modal.Root>
  )
})
